import {
  BarcodeGenerationType,
  BarcodeStatus,
} from '@buncha/driver/types/printScanBarcode';
import {H3Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ScanBarcodeAction,
  useScanBarcodeContext,
} from '@buncha/driver/hooks/ScanBarcode';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import BottomSheet from '@buncha/components/composites/bottomSheet';
import {Box} from '@buncha/components/core/view';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderInfo} from '@buncha/config/graphQL/type';
import {PButton} from '@buncha/components/core/Button';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {StyleSheet} from 'react-native';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';
import {useUpdateBarcodeStatus} from '@buncha/driver/hooks/printScanBarcodes';

interface TitleSubTitleBottomSheetProps {
  open: boolean;
  onClose: () => void;
  orderIds: number[];
  runId?: number;
  orderInfo?: OrderInfo[];
}

const UnableToScanBarcodeSheet: React.FC<
  TitleSubTitleBottomSheetProps
> = props => {
  const route = useRoute();
  const params:
    | {
        barcodeType?: BarcodeGenerationType;
      }
    | undefined = route?.params;
  const barcodeType = params?.barcodeType ?? BarcodeGenerationType.system;
  const theme = useColorThemeType('main');
  const mortise = useFontTheme('mortise');
  const muli = useFontTheme('muli');
  const [barcode, setBarcode] = useState('');
  const runId = props?.runId;
  const [updateBarcodeError, setUpdateBarcodeError] = useState<
    String | undefined
  >('');
  const {title, body, button} =
    useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const handleItemNotesChange = useCallback((text: string) => {
    setBarcode(text);
    setUpdateBarcodeError(undefined);
  }, []);
  const {state, dispatch} = useScanBarcodeContext();

  const barcodeCount = state.barcodeCount;
  const total = barcodeCount?.total;
  const scanned = barcodeCount?.scanned;

  const {updateBarcodeStatus, loading, errorMSG} = useUpdateBarcodeStatus(
    barcodeType,
    false,
  );

  useEffect(() => {
    if (errorMSG) setUpdateBarcodeError(errorMSG);
  }, [errorMSG]);

  const scanCallbackHandler = useCallback(() => {
    props?.onClose();
    if (total === Number(scanned || 0) + 1) {
      navigation.goBack();
    } else {
      const scannedCount = state.barcodeCount?.scanned ?? 0;
      const dataToUpdate = {
        total: state.barcodeCount?.total ?? 0,
        scanned: scannedCount + 1,
      };
      if (dispatch) dispatch(ScanBarcodeAction.setBarcodesCount(dataToUpdate));
      setBarcode('');
    }
  }, [
    dispatch,
    props,
    scanned,
    state.barcodeCount?.scanned,
    state.barcodeCount?.total,
    total,
  ]);

  const updateBarcode = useCallback(async () => {
    await updateBarcodeStatus(
      barcode,
      BarcodeStatus.scanned,
      props?.orderIds,
      scanCallbackHandler,
      runId || 0,
      props?.orderInfo || [],
    );
  }, [
    barcode,
    props?.orderIds,
    props?.orderInfo,
    runId,
    scanCallbackHandler,
    updateBarcodeStatus,
  ]);

  const getBorderColor = useMemo(() => {
    if (!barcode) return theme?.text?.gray?.[200];
    return theme?.text?.blue?.[300];
  }, [barcode, theme?.text?.blue, theme?.text?.gray]);

  return (
    <BottomSheet open={props?.open} onClose={props?.onClose}>
      <Box style={styles.parentBox}>
        <H3Text
          style={[
            styles.align,
            {
              color: theme?.text?.blue?.[300],
              fontFamily: mortise?.[700]?.normal,
            },
          ]}>
          {title?.unableToScan}
        </H3Text>
        <H6Text
          style={[
            styles.align,
            {color: theme?.text?.gray?.[100], fontFamily: muli?.[700]?.normal},
          ]}>
          {body?.unableToScan}
        </H6Text>
        <PTextInput
          value={barcode}
          placeholder="xxxx-xxxx-xxxx"
          debounce={false}
          clearInput
          returnKeyType="done"
          style={[styles.input, {color: theme?.text?.blue?.[300]}]}
          onChangeText={handleItemNotesChange}
          containerStyle={[
            styles.inputContainer,
            {
              borderColor: getBorderColor,
            },
          ]}
        />
        <If condition={updateBarcodeError}>
          <H6Text style={{color: theme?.text.red[100]}} numberOfLines={2}>
            {updateBarcodeError}
          </H6Text>
        </If>
        <PButton
          style={[styles.cta]}
          title={button?.addBarcode}
          onPress={updateBarcode}
          disabled={!barcode}
          isLoading={loading}
          loadingIndicator={<SpinnerComponent />}
        />
      </Box>
    </BottomSheet>
  );
};

export default React.memo(UnableToScanBarcodeSheet);

const styles = StyleSheet.create({
  parentBox: {
    padding: 15,
    gap: 15,
  },
  align: {
    textAlign: 'left',
  },
  cta: {
    borderRadius: 100,
  },
  input: {
    marginTop: 5,
  },
  inputContainer: {
    borderWidth: 1,
    width: '100%',
    borderRadius: 15,
    marginTop: 10,
  },
});
